import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "@/App.css";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AuthPage from "@/pages/AuthPage";
import Terms from "@/pages/Terms";
import Dashboard from "@/pages/Dashboard";
import Trades from "@/pages/Trades";
import TradeDetail from "@/pages/TradeDetail";
import ImportPage from "@/pages/ImportPage";
import Analytics from "@/pages/Analytics";
import Coach from "@/pages/Coach";
import Journal from "@/pages/Journal";
import Recap from "@/pages/Recap";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Connections from "@/pages/Connections";
import TraderCard from "@/pages/TraderCard";
import Calendar from "@/pages/Calendar";
import ContactUs from "@/pages/ContactUs";
import AdminRestore from "@/pages/AdminRestore";
import ReplayPage from "@/pages/ReplayPage";
import WeeklyReel from "@/pages/WeeklyReel";
import Library from "@/pages/Library";
import SharedClip from "@/pages/SharedClip";
import Partners from "@/pages/Partners";
import PartnerDashboard from "@/pages/PartnerDashboard";
import Shell from "@/components/Shell";

const StuckLoader = () => {
  // Shows LOADING for the first 6 seconds. If the backend never responds, surfaces
  // rescue buttons so the customer is NEVER trapped in an infinite loader — they can
  // always sign out or force-continue to the pricing page and self-heal via
  // "ALREADY PAID? RESTORE ACCESS". This directly addresses the one paying customer
  // who was stuck on infinite LOADING after login (Feb 18, 2026).
  const [showRescue, setShowRescue] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setShowRescue(true), 6000);
    return () => clearTimeout(t);
  }, []);
  const signOut = () => {
    try { localStorage.removeItem("edge_token"); } catch { /* ignore */ }
    try { localStorage.removeItem("edge_last_good_billing"); } catch { /* ignore */ }
    window.location.href = "/auth";
  };
  const forceContinue = () => {
    window.location.href = "/pricing";
  };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" style={{ background: "var(--bg)", color: "var(--fg-2)", fontFamily: "var(--font-body)" }}>
      <div className="text-sm font-medium" style={{ color: "var(--fg-2)" }}>Loading your dashboard…</div>
      {showRescue && (
        <div className="max-w-md text-center space-y-4" data-testid="stuck-loader-rescue">
          <div className="text-xs" style={{ color: "var(--fg-3)" }}>
            Taking longer than usual. If this keeps hanging, use one of the options below.
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={forceContinue}
              data-testid="stuck-continue-btn"
              className="px-4 py-2 text-sm font-medium transition-colors"
              style={{ background: "var(--green)", color: "#08080A", borderRadius: "10px" }}
            >
              Continue to pricing
            </button>
            <button
              onClick={signOut}
              data-testid="stuck-signout-btn"
              className="px-4 py-2 text-sm font-medium transition-colors"
              style={{ background: "transparent", color: "var(--fg)", border: "1px solid var(--border)", borderRadius: "10px" }}
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Paywalled = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { data: billing, isLoading: billingLoading, isError: billingError } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => {
      const r = await api.get("/billing/status");
      // Cache last-good billing state so a subsequent transient failure never
      // kicks a paying user to /pricing. Cleared on explicit sign-out.
      try {
        if (r?.data?.has_access) {
          localStorage.setItem("edge_last_good_billing", JSON.stringify({
            at: Date.now(),
            has_access: true,
          }));
        }
      } catch { /* localStorage full or blocked — ignore */ }
      return r.data;
    },
    enabled: !!user,
    // More retries + longer window before failing — a slow Stripe reconcile or
    // brief network blip should NEVER bounce a logged-in customer to pricing.
    retry: 3,
    retryDelay: (attempt) => Math.min(2000 * (attempt + 1), 8000),
  });

  // If /billing/status is currently erroring but we have a cached "has_access"
  // from the last successful call within the last 24 hours, treat the user as
  // still having access. This is the fail-open safety net that stops the
  // "randomly bounced to /pricing" bug that customers reported.
  const lastGoodBilling = (() => {
    try {
      const raw = localStorage.getItem("edge_last_good_billing");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ageMs = Date.now() - (parsed?.at || 0);
      if (parsed?.has_access && ageMs < 24 * 60 * 60 * 1000) return parsed;
    } catch { /* ignore */ }
    return null;
  })();

  // Recent-good billing (within the last 60 min) is trusted more strongly:
  // if a real /billing/status just returned has_access=false but we KNOW the
  // user had access a minute ago, that's almost always a Stripe reconcile
  // race (webhook mid-flight). Ignore the false and let them in. Prevents
  // paying customers from getting bounced to /pricing during transient
  // Stripe hiccups.
  const recentlyPaidUp = (() => {
    try {
      const raw = localStorage.getItem("edge_last_good_billing");
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      const ageMs = Date.now() - (parsed?.at || 0);
      return !!(parsed?.has_access && ageMs < 60 * 60 * 1000);
    } catch { return false; }
  })();

  // Never gate the app on `billingLoading`. Once the user is authenticated,
  // they should NEVER see a full-screen loader again during their session —
  // even if billing-status is refetching in the background (network blip,
  // stale-while-revalidate, action-triggered invalidation). Previously we
  // rendered <StuckLoader/> whenever billing was fetching, which caused the
  // "stuck on loading screen after any action" bug.
  if (loading) return <StuckLoader />;
  if (!user) return <Navigate to="/auth" replace state={{ from: location.pathname }} />;

  // GRACE PERIOD: NEVER hard-redirect a signed-in user to /pricing on a single
  // billing check. A paying customer getting bounced to the pricing page mid-
  // trading-session is one of the worst things a SaaS can do — even if their
  // subscription genuinely lapsed, they deserve a soft warning banner, not an
  // interruption. We only redirect if:
  //   1. The billing endpoint SUCCEEDED with has_access=false
  //   2. AND they weren't paying within the last hour (no Stripe reconcile race)
  //   3. AND they have NEVER had a good billing state cached at all
  //      (a true "no-access-forever" user — never paid, trial ended, etc.)
  //
  // Otherwise: render the app normally. If they lost real access, the sidebar
  // and settings pages surface the "Payment needed" banner. No kick-outs.
  if (billing && !billing.has_access && !recentlyPaidUp && !lastGoodBilling) {
    return <Navigate to="/pricing" replace />;
  }
  // Even if billingError, DON'T kick — just render the app. The user can retry
  // themselves. Better a broken screen than a frustrated paying customer.
  return <Shell>{children}</Shell>;
};

const AuthOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-500 font-mono-num text-xs">LOADING…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

function App() {
  // Capture ?ref=CODE from ANY landing URL and stash in localStorage so the
  // partner attribution survives auth redirects and page navigations. Read by
  // /pricing at checkout time.
  React.useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) {
        window.localStorage.setItem("tg_partner_ref", ref.trim().toUpperCase());
      }
    } catch { /* SSR / disabled localStorage — no-op */ }
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pricing" element={<AuthOnly><Pricing /></AuthOnly>} />
        <Route path="/billing/success" element={<AuthOnly><BillingSuccess /></AuthOnly>} />
        <Route path="/" element={<Paywalled><Dashboard /></Paywalled>} />
        <Route path="/trades" element={<Paywalled><Trades /></Paywalled>} />
        <Route path="/trades/:id" element={<Paywalled><TradeDetail /></Paywalled>} />
        <Route path="/import" element={<Paywalled><ImportPage /></Paywalled>} />
        <Route path="/analytics" element={<Paywalled><Analytics /></Paywalled>} />
        <Route path="/journal" element={<Paywalled><Journal /></Paywalled>} />
        <Route path="/recap" element={<Paywalled><Recap /></Paywalled>} />
        <Route path="/recap/:date" element={<Paywalled><Recap /></Paywalled>} />
        <Route path="/coach" element={<Paywalled><Coach /></Paywalled>} />
        <Route path="/connections" element={<Paywalled><Connections /></Paywalled>} />
        <Route path="/share" element={<Paywalled><TraderCard /></Paywalled>} />
        <Route path="/calendar" element={<Paywalled><Calendar /></Paywalled>} />
        <Route path="/contact" element={<Paywalled><ContactUs /></Paywalled>} />
        <Route path="/replay" element={<Paywalled><ReplayPage /></Paywalled>} />
        <Route path="/reel" element={<Paywalled><WeeklyReel /></Paywalled>} />
        <Route path="/library" element={<Paywalled><Library /></Paywalled>} />
        <Route path="/clip/:token" element={<SharedClip />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/partners/dashboard" element={<AuthOnly><PartnerDashboard /></AuthOnly>} />
        <Route path="/admin/restore" element={<AuthOnly><AdminRestore /></AuthOnly>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
